import { createElement, lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { CHUNK_RELOAD_KEY } from '@constants/storage';
import { CHUNK_RELOAD_GUARD_MS } from '@constants/timing';

/** When the last chunk-failure reload happened (epoch ms), kept across that reload. */
const lastReload = {
  get: () => {
    try {
      return Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
    } catch {
      return 0;
    }
  },
  set: (at: number) => {
    try {
      window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(at));
    } catch {
      /* storage unavailable: the guard window cannot hold, worst case one extra reload */
    }
  },
};

/**
 * After a deploy, a tab opened before it asks for chunks that no longer exist: reload to get the new
 * index.html. Not when offline (the reload would land on the browser's offline page), and not again right
 * after such a reload (a chunk that is really broken would loop); then the error reaches the route boundary.
 */
function recover(e: unknown): Promise<never> {
  if (!navigator.onLine || Date.now() - lastReload.get() < CHUNK_RELOAD_GUARD_MS) throw e;
  lastReload.set(Date.now());
  window.location.reload();
  return new Promise(() => undefined);
}

/** A route component loaded on demand. `load` is also used to prefetch the chunk while the browser is idle. */
export function lazyPage<P extends object>(load: () => Promise<ComponentType<P>>) {
  // React.lazy keeps a rejection forever: after a failure start a fresh one, so coming back to the page retries
  const make = (): LazyExoticComponent<ComponentType<P>> =>
    lazy(() =>
      load().then(
        (C) => ({ default: C }),
        (e) => {
          current = make();
          return recover(e);
        },
      ),
    );
  let current = make();
  return function LazyPage(props: P) {
    return createElement(current as ComponentType<P>, props);
  };
}
